import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAuthData, setAuthData, clearAuthData } from '../lib/auth';
import api from '../lib/api';

const LEVELS = ['none', 'view', 'edit', 'full'];

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Role-wise access levels per CRM area, from /permissions/me. Until they
  // load everything is shown; the API enforces access either way.
  const [levels, setLevels] = useState(null);
  // Sales, revenue, prices and order totals hidden for this role.
  const [hideNumbers, setHideNumbers] = useState(false);

  useEffect(() => {
    if (!user) { setLevels(null); setHideNumbers(false); return; }
    let cancelled = false;
    api.get('/permissions/me')
      .then((res) => { if (!cancelled) { setLevels(res.data?.levels || null); setHideNumbers(!!res.data?.hide_numbers); } })
      .catch(() => { if (!cancelled) setLevels(null); });
    return () => { cancelled = true; };
  }, [user]);

  const can = useCallback((area, level = 'view') => {
    if (!area || !levels) return true;
    return LEVELS.indexOf(levels[area] || 'none') >= LEVELS.indexOf(level);
  }, [levels]);

  useEffect(() => {
    try {
      const { token, user: savedUser } = getAuthData();
      if (token && savedUser) {
        setUser(savedUser);
      } else if (token && !savedUser) {
        // We have a token but no user payload — clear and force re-login.
        // (This is the recovery path for the old "undefined" localStorage bug.)
        clearAuthData();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('AuthProvider init failed, clearing auth state:', e);
      try {
        clearAuthData();
      } catch (_) {
        /* ignore */
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    if (!token || !userData) {
      // Defensive: never persist a half-baked auth state.
      // eslint-disable-next-line no-console
      console.warn('login() called with missing token or user; ignoring.', {
        hasToken: !!token,
        hasUser: !!userData,
      });
      return;
    }
    setAuthData(token, userData);
    setUser(userData);
  };

  const logout = () => {
    clearAuthData();
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    loading,
    can,
    hideNumbers,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
